import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, FileText, DollarSign, Briefcase, ChevronDown, Ruler } from "lucide-react";
import { api } from "@/lib/api";
import { ProposalsTab } from "@/components/admin/ProposalsTab";
import { JobsTab } from "@/components/admin/JobsTab";
import { LibraryTab } from "@/components/admin/LibraryTab";
import { CrewPanel, PayrollPanel } from "@/components/admin/GeneralPanels";
import { PaymentsPanel } from "@/components/admin/PaymentsPanel";
import { MeasurementsTab } from "@/components/admin/MeasurementsTab";
import { ProjectPicturesPanel } from "@/components/admin/ProjectPicturesPanel";

const Collapsible = ({ id, title, hint, defaultOpen = false, remember = true, children }) => {
  const [open, setOpen] = useState(() => {
    if (!remember) return defaultOpen;
    try {
      const v = localStorage.getItem(`collapse-${id}`);
      return v === null ? defaultOpen : v === "1";
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (remember) { try { localStorage.setItem(`collapse-${id}`, next ? "1" : "0"); } catch {} }
  };
  return (
    <div data-testid={`${id}-section`}>
      <button onClick={toggle} data-testid={`${id}-toggle`} aria-expanded={open} className="flex items-center justify-between w-full text-left gap-3 group">
        <div>
          <h2 className="font-display text-2xl tracking-wide group-hover:text-primary transition-colors duration-300">{title}</h2>
          <p className="text-white/40 text-xs mt-1">{hint}{!open ? " — tap to open" : ""}</p>
        </div>
        <ChevronDown size={20} className={`shrink-0 text-white/40 group-hover:text-primary transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
};
import { ScheduleCalendar } from "@/components/ScheduleCalendar";

const TABS = [
  { key: "measure", label: "Measure", icon: Ruler },
  { key: "proposals", label: "Proposals", icon: FileText },
  { key: "jobs", label: "Jobs", icon: Briefcase },
  { key: "payroll", label: "Payroll", icon: DollarSign },
  { key: "general", label: "General", icon: Inbox },
];

const QUOTE_FLOW = ["new", "contacted", "quoted", "won", "lost"];

const STATUS_STYLES = {
  new: { label: "New", badge: "bg-primary text-primary-foreground", btn: "border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground" },
  contacted: { label: "Contacted", badge: "bg-sky-400/20 text-sky-300", btn: "border-sky-400/40 text-sky-300 hover:bg-sky-400 hover:text-black" },
  quoted: { label: "Quoted", badge: "bg-amber-400/20 text-amber-300", btn: "border-amber-400/40 text-amber-300 hover:bg-amber-400 hover:text-black" },
  won: { label: "Won", badge: "bg-emerald-400/20 text-emerald-300", btn: "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400 hover:text-black" },
  lost: { label: "Lost", badge: "bg-red-400/20 text-red-300", btn: "border-red-400/40 text-red-300 hover:bg-red-400 hover:text-black" },
};

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("measure");
  const [quotes, setQuotes] = useState([]);
  const [quoteFilter, setQuoteFilter] = useState("all");

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const loadQuotes = useCallback(() => {
    api.get("/admin/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    if (tab === "general") loadQuotes();
  }, [tab, user, loadQuotes]);

  useEffect(() => {
    const handler = (e) => setTab(e.detail);
    window.addEventListener("admin-goto-tab", handler);
    return () => window.removeEventListener("admin-goto-tab", handler);
  }, []);

  const setQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch {
      toast.error("Could not update");
    }
  };

  const logout = () => {
    localStorage.removeItem("is_token");
    navigate("/admin/login");
  };

  if (!user) return <div className="min-h-screen bg-background flex items-center justify-center text-white/50 text-sm">Loading...</div>;

  const visibleQuotes = quoteFilter === "all" ? quotes : quotes.filter((q) => q.status === quoteFilter);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background pb-28">
      <header className="sticky top-0 z-40 bg-[#171914]/85 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10 px-5 py-4 flex items-center justify-between">
        <img src="/logo.png" alt="Infinite Siding" className="h-9 w-auto logo-on-dark" />
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-primary transition-colors duration-300">
          <LogOut size={15} aria-hidden="true" /> Logout
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-5 pt-8">
        {tab === "measure" && <MeasurementsTab />}

        {tab === "proposals" && <ProposalsTab />}

        {tab === "jobs" && <JobsTab />}

        {tab === "payroll" && (
          <div data-testid="admin-payroll-tab" className="space-y-8">
            <div>
              <h1 className="font-display text-4xl tracking-wide">PAYROLL</h1>
              <p className="text-white/40 text-xs mt-1">Review and approve hours, set deductions, export the report — and schedule the crew on the calendar below.</p>
            </div>
            <Collapsible id="payroll-hours" title="HOURS & PAYROLL" hint="Review hours, approve, mark paid, deductions, CSV export" defaultOpen={true}>
              <PayrollPanel />
            </Collapsible>
            <Collapsible id="payroll-crew" title="CREW" hint="Add workers, set wages, create foreman PINs" defaultOpen={true}>
              <CrewPanel onChanged={() => {}} />
            </Collapsible>
            <Collapsible id="payments-report" title="PAYMENTS & REPORTS" hint="Payments ledger, monthly and annual reports">
              <PaymentsPanel />
            </Collapsible>
            <div>
              <h2 className="font-display text-2xl tracking-wide mb-1">WORK CALENDAR</h2>
              <p className="text-white/40 text-xs mb-4">Tap a day, pick a project — its foreman is added automatically and sees it on his calendar.</p>
              <ScheduleCalendar mode="admin" />
            </div>
          </div>
        )}

        {tab === "general" && (
          <div data-testid="admin-general-tab" className="space-y-8">
            <h1 className="font-display text-4xl tracking-wide">GENERAL</h1>
            <Collapsible id="general-pictures" title="PROJECT PICTURES" hint="Website gallery — photos and videos" remember={false}>
              <ProjectPicturesPanel />
            </Collapsible>
            <Collapsible id="general-library" title="MATERIALS LIBRARY" hint="Items, categories, prices and photos" remember={false}>
              <LibraryTab />
            </Collapsible>
            <Collapsible id="general-quotes" title="QUOTE REQUESTS" hint={`${quotes.filter((q) => q.status === "new").length} new · ${quotes.length} total`} remember={false}>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="quote-status-filters">
              {["all", ...QUOTE_FLOW].map((s) => (
                <button
                  key={s}
                  data-testid={`quote-filter-${s}`}
                  onClick={() => setQuoteFilter(s)}
                  className={`px-4 py-2 text-[11px] font-bold uppercase tracking-widest border transition-colors duration-300 ${
                    quoteFilter === s ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white/60"
                  }`}
                >
                  {s}{s !== "all" && ` (${quotes.filter((q) => q.status === s).length})`}
                </button>
              ))}
            </div>
            {visibleQuotes.length === 0 && <p className="text-white/40 text-sm py-10 text-center" data-testid="admin-quotes-empty">No quote requests here yet.</p>}
            <div className="space-y-3">
              {visibleQuotes.map((q) => {
                const cfg = STATUS_STYLES[q.status] || STATUS_STYLES.new;
                return (
                  <div key={q.id} className={`border rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.3)] p-5 ${q.status === "new" ? "border-primary/50 bg-primary/5" : "border-white/10 bg-card"}`} data-testid={`admin-quote-${q.id}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${q.type === "commercial" ? "bg-primary text-primary-foreground" : "bg-white/15 text-white"}`}>{q.type}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${cfg.badge}`} data-testid={`quote-status-${q.id}`}>{q.status}</span>
                      </div>
                      <span className="text-[11px] text-white/40">{new Date(q.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="font-bold text-base">{q.name}{q.company ? ` — ${q.company}` : ""}</p>
                    <p className="text-sm text-white/70 mt-1">
                      <a href={`mailto:${q.email}`} className="text-primary">{q.email}</a>{q.phone ? ` · ` : ""}{q.phone && <a href={`tel:${q.phone}`} className="text-primary">{q.phone}</a>}
                    </p>
                    <p className="text-xs text-white/50 mt-1">{[q.address, q.location, q.service, q.size, q.timeline].filter(Boolean).join(" · ")}</p>
                    {q.message && <p className="text-sm text-white/70 mt-2 leading-relaxed">{q.message}</p>}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {QUOTE_FLOW.filter((s) => s !== q.status).map((s) => (
                        <button
                          key={s}
                          onClick={() => setQuoteStatus(q.id, s)}
                          data-testid={`quote-set-${s}-${q.id}`}
                          className={`px-3.5 py-2 text-[10px] font-bold uppercase tracking-widest border transition-colors duration-300 ${STATUS_STYLES[s].btn}`}
                        >
                          {STATUS_STYLES[s].label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            </Collapsible>
          </div>
        )}
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 bg-[#171914]/90 backdrop-blur-xl border-t border-white/10 grid grid-cols-5" data-testid="admin-bottom-nav">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`py-4 flex flex-col items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 ${tab === t.key ? "text-primary" : "text-white/50"}`}
          >
            <t.icon size={20} aria-hidden="true" />
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
